/**
 * Role pills are grouped and locked by role CODE (the Role.name column, e.g. WAREHOUSE_STAFF).
 *
 * GET /prt/general/init sends roles as `{...role, name: <translated label>}` with no `code`,
 * so the code is recovered from the label through the table below, a copy of the label column of
 * sequelize-models `CONSTANTS.ROLE_NAMES`. A role whose label is not in the table is its own code
 * (the backend leaves untranslated names raw, e.g. ORDER_APPROVAL_STAFF). When the backend starts
 * sending `code`, that wins and this table becomes a fallback.
 */

export const ROLE_CODE = {
  DEHUB_MANAGER: "DEHUB_MANAGER",
  DEHUB_FINANCE: "DEHUB_FINANCE",
  FINANCE_MANAGER: "FINANCE_MANAGER",
} as const;

const LABEL_TO_CODE: Record<string, string> = {
  "Dehub Менежер": "DEHUB_MANAGER",
  "Dehub Санхүү": "DEHUB_FINANCE",
  "Тенант админ": "TENANT_ADMIN",
  Finance: "FINANCE_MANAGER",
  Manager: "SALES_MANAGER",
  "Ачилтын нярав": "PULL_SHEET_STAFF",
  "Хүргэлтийн ажилтан": "DELIVERY_STAFF",
  "Хүргэлтийн супервайзер": "DELIVERY_SUPERVISOR",
  "Агуулахын нярав": "WAREHOUSE_STAFF",
  "Систем оператор": "REGISTRATION_STAFF",
  "Гэрээ байгуулагч": "CONTRACTER_STAFF",
  "Гэрээ бүртгэх ажилтан": "CONTRACT_REGISTER_STAFF",
  "Гэрээ хянагч": "CONTRACT_REVIEWER",
  // ROLE_NAMES.CHANGE_APPROVAL_STAFF has this label but its key differs from its code, so the backend
  // does not translate it today (the label stays "ORDER_APPROVAL_STAFF"); mapped for when it does.
  "Өөрчлөлт батлах ажилтан": "ORDER_APPROVAL_STAFF",
  "Байршуулалтын админ": "CONSIGNMENT_ADMIN",
  "Байршуулалт — Нийлүүлэгч менежер": "CONSIGNMENT_SUPPLIER_MANAGER",
  "Байршуулалт — Худалдагч менежер": "CONSIGNMENT_RETAILER_MANAGER",
  "Байршуулалт — Тооллогын ажилтан": "CONSIGNMENT_AUDITOR",
  "Байршуулалт — Зөвхөн харах": "CONSIGNMENT_VIEWER",
};

/**
 * Display name by role code, for roles the backend leaves in English or untranslated
 * (Manager, Finance, ORDER_APPROVAL_STAFF); any other role shows its own label.
 */
const ROLE_LABEL_MN: Record<string, string> = {
  DEHUB_MANAGER: "DeHUB менежер",
  DEHUB_FINANCE: "DeHUB санхүү",
  SALES_MANAGER: "Менежер",
  FINANCE_MANAGER: "Санхүү",
  ORDER_APPROVAL_STAFF: "Өөрчлөлт батлах ажилтан",
};

/** The backend prefixes consignment roles with their group ("Байршуулалт — Зөвхөн харах"). */
const CONSIGNMENT_PREFIX = /^Байршуулалт\s*[—–-]\s*/;

export type RoleLike = { id: string; name: string; code?: string; roleType?: string };

export function roleCodeOf(role: RoleLike): string {
  return role.code ?? LABEL_TO_CODE[role.name] ?? role.name;
}

/**
 * Name of a role. Inside the grouped pills the group is already a heading, so the consignment
 * prefix is dropped; `withGroup` (table chips, messages) shows it as "Байршуулалт: Зөвхөн харах".
 */
export function roleLabel(role: RoleLike, withGroup = false): string {
  const fixed = ROLE_LABEL_MN[roleCodeOf(role)];
  if (fixed) return fixed;
  if (CONSIGNMENT_PREFIX.test(role.name)) {
    const bare = role.name.replace(CONSIGNMENT_PREFIX, "");
    return withGroup ? `Байршуулалт: ${bare}` : bare;
  }
  return role.name;
}

export type RoleGroupId = "dehub" | "management" | "warehouse" | "contract" | "consignment" | "other";

/** In display order. DEHUB roles are only assigned by a DeHUB manager / finance user. */
export const ROLE_GROUPS: { id: RoleGroupId; codes: string[] }[] = [
  { id: "dehub", codes: ["DEHUB_MANAGER", "DEHUB_FINANCE"] },
  { id: "management", codes: ["SALES_MANAGER", "FINANCE_MANAGER", "REGISTRATION_STAFF", "ORDER_APPROVAL_STAFF", "TENANT_ADMIN"] },
  { id: "warehouse", codes: ["WAREHOUSE_STAFF", "PULL_SHEET_STAFF", "DELIVERY_STAFF", "DELIVERY_SUPERVISOR"] },
  { id: "contract", codes: ["CONTRACTER_STAFF", "CONTRACT_REGISTER_STAFF", "CONTRACT_REVIEWER"] },
  {
    id: "consignment",
    codes: [
      "CONSIGNMENT_ADMIN",
      "CONSIGNMENT_SUPPLIER_MANAGER",
      "CONSIGNMENT_RETAILER_MANAGER",
      "CONSIGNMENT_AUDITOR",
      "CONSIGNMENT_VIEWER",
    ],
  },
];

export type RoleGroup<T extends RoleLike> = { id: RoleGroupId; roles: T[] };

/** Every role lands in exactly one group; roles no group knows go to "other". Empty groups are dropped. */
export function groupRoles<T extends RoleLike>(roles: T[]): RoleGroup<T>[] {
  const groups: RoleGroup<T>[] = ROLE_GROUPS.map((group) => ({
    id: group.id,
    roles: group.codes.flatMap((code) => roles.filter((role) => roleCodeOf(role) === code)),
  }));
  const known = new Set(ROLE_GROUPS.flatMap((group) => group.codes));
  groups.push({ id: "other", roles: roles.filter((role) => !known.has(roleCodeOf(role))) });
  return groups.filter((group) => group.roles.length > 0);
}

type Flags = { isDehubManager?: boolean; isDehubFinance?: boolean };

/**
 * Whether the current user may not assign (or remove) a role at all.
 * - DEHUB_MANAGER / DEHUB_FINANCE: only a DeHUB manager / finance user.
 * - MANAGER / FINANCE role types: the existing gate, unchanged (`=== false`, so an unknown flag allows).
 */
export function isRoleLocked(role: RoleLike, flags: Flags): boolean {
  const code = roleCodeOf(role);
  if (code === ROLE_CODE.DEHUB_MANAGER && flags.isDehubManager !== true) return true;
  if (code === ROLE_CODE.DEHUB_FINANCE && flags.isDehubFinance !== true) return true;
  if (role.roleType === "MANAGER" && flags.isDehubManager === false) return true;
  if (role.roleType === "FINANCE" && flags.isDehubFinance === false) return true;
  return false;
}

const FINANCE_CODES: string[] = [ROLE_CODE.DEHUB_FINANCE, ROLE_CODE.FINANCE_MANAGER];

/**
 * The already selected role codes that cannot stay selected together with `code` (the old filterRoles
 * rule, by code): DEHUB_MANAGER excludes the finance roles; DEHUB_FINANCE excludes every non-finance
 * role; and the other way round.
 */
export function conflictingCodes(code: string, selectedCodes: ReadonlySet<string>): string[] {
  const selected = Array.from(selectedCodes);
  if (code === ROLE_CODE.DEHUB_MANAGER) return selected.filter((c) => FINANCE_CODES.includes(c));
  if (code === ROLE_CODE.DEHUB_FINANCE) return selected.filter((c) => !FINANCE_CODES.includes(c));
  if (FINANCE_CODES.includes(code)) return selected.filter((c) => c === ROLE_CODE.DEHUB_MANAGER);
  return selected.filter((c) => c === ROLE_CODE.DEHUB_FINANCE);
}

/** Dev-only sanity check: every role should land in one of the five groups (18 on DEV). */
export function warnUnexpectedRoles<T extends RoleLike>(roles: T[], groups: RoleGroup<T>[]): void {
  if (process.env.NODE_ENV === "production" || roles.length === 0) return;
  const other = groups.find((group) => group.id === "other");
  if (roles.length !== 18 || (other && other.roles.length > 0)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[business/users] expected 18 roles in 5 groups, got ${roles.length}; ungrouped: ${(other?.roles ?? [])
        .map((role) => `${role.name} (${roleCodeOf(role)})`)
        .join(", ") || "none"}`,
    );
  }
}
